import { useCallback } from 'react'
import type { UserAnswerDraft, UserAnswersDraft } from '../../state/types'
import { updateTimestamps } from '../../state/types'

interface UseUserAnswersDraftsParams {
  readonly userAnswersDraft: UserAnswersDraft | null
  readonly onUserAnswersDraftChange: (draft: UserAnswersDraft | null) => void
  readonly eventId: string
}

interface SaveAnswerParams {
  readonly questionHeader: string
  readonly questionText: string
  readonly selectedIndices: readonly number[]
  readonly selectedAnswers: readonly string[]
  readonly customAnswer: string | null
}

interface UseUserAnswersDraftsReturn {
  readonly saveAnswer: (params: SaveAnswerParams) => void
  readonly saveCustomAnswer: (questionHeader: string, content: string) => void
  readonly deleteAnswer: (questionHeader: string) => void
  readonly deleteCustomAnswer: (questionHeader: string) => void
  readonly getAnswer: (questionHeader: string) => UserAnswerDraft | null
  readonly getCustomAnswer: (questionHeader: string) => string | null
  readonly clearAllAnswers: () => void
  readonly saveBulkAnswers: (answers: ReadonlyMap<string, UserAnswerDraft>) => void
}

function useUserAnswersDrafts({
  userAnswersDraft,
  onUserAnswersDraftChange,
  eventId,
}: UseUserAnswersDraftsParams): UseUserAnswersDraftsReturn {
  const isMatchingEvent = useCallback(
    () => userAnswersDraft?.eventId === eventId,
    [userAnswersDraft?.eventId, eventId],
  )

  const persistAnswer = useCallback(
    (questionHeader: string, newAnswer: UserAnswerDraft) => {
      if (!isMatchingEvent() || !userAnswersDraft) {
        const newDraft: UserAnswersDraft = {
          eventId,
          answers: { [questionHeader]: newAnswer },
          updatedAt: Date.now(),
        }
        onUserAnswersDraftChange(newDraft)
        return
      }

      const updatedDraft: UserAnswersDraft = {
        ...userAnswersDraft,
        answers: {
          ...userAnswersDraft.answers,
          [questionHeader]: newAnswer,
        },
        updatedAt: Date.now(),
      }
      onUserAnswersDraftChange(updatedDraft)
    },
    [userAnswersDraft, onUserAnswersDraftChange, eventId, isMatchingEvent],
  )

  const saveAnswer = useCallback(
    ({
      questionHeader,
      questionText,
      selectedIndices,
      selectedAnswers,
      customAnswer,
    }: SaveAnswerParams) => {
      const existingAnswer = isMatchingEvent()
        ? userAnswersDraft?.answers[questionHeader]
        : undefined

      const newAnswer: UserAnswerDraft = {
        question: questionText,
        selectedIndices: [...selectedIndices],
        selectedAnswers: [...selectedAnswers],
        customAnswer,
        ...updateTimestamps(existingAnswer),
      }

      persistAnswer(questionHeader, newAnswer)
    },
    [userAnswersDraft, isMatchingEvent, persistAnswer],
  )

  const saveCustomAnswer = useCallback(
    (questionHeader: string, content: string) => {
      const existingAnswer = isMatchingEvent()
        ? userAnswersDraft?.answers[questionHeader]
        : undefined

      const newAnswer: UserAnswerDraft = {
        question: questionHeader,
        selectedIndices: existingAnswer?.selectedIndices ?? [],
        selectedAnswers: existingAnswer?.selectedAnswers ?? [],
        customAnswer: content,
        ...updateTimestamps(existingAnswer),
      }

      persistAnswer(questionHeader, newAnswer)
    },
    [userAnswersDraft, isMatchingEvent, persistAnswer],
  )

  const deleteAnswer = useCallback(
    (questionHeader: string) => {
      if (!userAnswersDraft || !isMatchingEvent()) return

      const remainingAnswers = Object.fromEntries(
        Object.entries(userAnswersDraft.answers).filter(([key]) => key !== questionHeader),
      )

      if (Object.keys(remainingAnswers).length === 0) {
        onUserAnswersDraftChange(null)
        return
      }

      const updatedDraft: UserAnswersDraft = {
        ...userAnswersDraft,
        answers: remainingAnswers,
        updatedAt: Date.now(),
      }
      onUserAnswersDraftChange(updatedDraft)
    },
    [userAnswersDraft, onUserAnswersDraftChange, isMatchingEvent],
  )

  const deleteCustomAnswer = useCallback(
    (questionHeader: string) => {
      if (!userAnswersDraft || !isMatchingEvent()) return

      const existingAnswer = userAnswersDraft.answers[questionHeader]
      if (!existingAnswer) return

      const updatedDraft: UserAnswersDraft = {
        ...userAnswersDraft,
        answers: {
          ...userAnswersDraft.answers,
          [questionHeader]: {
            ...existingAnswer,
            customAnswer: null,
            ...updateTimestamps(existingAnswer),
          },
        },
        updatedAt: Date.now(),
      }
      onUserAnswersDraftChange(updatedDraft)
    },
    [userAnswersDraft, onUserAnswersDraftChange, isMatchingEvent],
  )

  const getAnswer = useCallback(
    (questionHeader: string): UserAnswerDraft | null => {
      if (!isMatchingEvent()) return null
      return userAnswersDraft?.answers[questionHeader] ?? null
    },
    [userAnswersDraft, isMatchingEvent],
  )

  const getCustomAnswer = useCallback(
    (questionHeader: string): string | null => {
      if (!isMatchingEvent()) return null
      return userAnswersDraft?.answers[questionHeader]?.customAnswer ?? null
    },
    [userAnswersDraft, isMatchingEvent],
  )

  const clearAllAnswers = useCallback(() => {
    onUserAnswersDraftChange(null)
  }, [onUserAnswersDraftChange])

  const saveBulkAnswers = useCallback(
    (answers: ReadonlyMap<string, UserAnswerDraft>) => {
      if (answers.size === 0) {
        onUserAnswersDraftChange(null)
        return
      }

      const answersRecord: Record<string, UserAnswerDraft> = {}
      for (const [key, answer] of answers) {
        answersRecord[key] = answer
      }

      const newDraft: UserAnswersDraft = {
        eventId,
        answers: answersRecord,
        updatedAt: Date.now(),
      }
      onUserAnswersDraftChange(newDraft)
    },
    [eventId, onUserAnswersDraftChange],
  )

  return {
    saveAnswer,
    saveCustomAnswer,
    deleteAnswer,
    deleteCustomAnswer,
    getAnswer,
    getCustomAnswer,
    clearAllAnswers,
    saveBulkAnswers,
  } as const
}

export { useUserAnswersDrafts }
export type { UseUserAnswersDraftsParams, UseUserAnswersDraftsReturn, SaveAnswerParams }
